import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Play, User, Clock, MapPin, Note } from '@phosphor-icons/react';

const statusColors = {
  'Yeni': 'bg-[hsl(var(--primary))] text-white',
  'İnceleniyor': 'bg-[hsl(var(--accent))] text-black',
  'İşleme Alındı': 'bg-blue-600 text-white',
  'Kapatıldı': 'bg-[hsl(var(--success))] text-white',
};

export default function OfficerReportDetail() {
  const { id } = useParams();
  const { user, token, authAxios } = useAuth();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [updating, setUpdating] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);

  const API = '/api';

  const fetchReport = useCallback(() => {
    authAxios().get(`/reports/${id}`)
      .then(res => {
        setReport(res.data);
        setNewStatus(res.data.status);
      })
      .catch(() => toast.error('İhbar yüklenemedi'))
      .finally(() => setLoading(false));
  }, [authAxios, id]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Fetch video as blob for secure playback
  useEffect(() => {
    if (report?.video_path && !report.video_deleted) {
      fetch(`${API}/video/${report.video_path}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => {
          if (res.ok) return res.blob();
          throw new Error('Video fetch failed');
        })
        .then(blob => {
          const url = URL.createObjectURL(blob);
          setVideoUrl(url);
        })
        .catch(() => setVideoUrl(null));
    }
    return () => { if (videoUrl) URL.revokeObjectURL(videoUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.video_path, token, API]);

  const handleStatusUpdate = async () => {
    if (!newStatus || newStatus === report.status) return;
    setUpdating(true);
    try {
      await authAxios().put(`/reports/${id}/status`, { status: newStatus, note: statusNote });
      toast.success('Durum güncellendi');
      setStatusNote('');
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncelleme başarısız');
    } finally { setUpdating(false); }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const backPath = user?.role === 'admin' ? '/admin' : '/officer';

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-[hsl(var(--muted-foreground))]">Yükleniyor...</div>;
  if (!report) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-[hsl(var(--muted-foreground))]">İhbar bulunamadı</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="bg-[#1C1C1E] border-b border-[#2C2C2E] px-4 py-3 flex items-center gap-3">
        <Button
          data-testid="back-button"
          variant="ghost"
          onClick={() => navigate(backPath)}
          className="text-white hover:bg-[#2C2C2E] h-10 w-10 p-0"
        >
          <ArrowLeft weight="bold" className="w-5 h-5" />
        </Button>
        <h1 className="font-heading text-lg text-white">{report.report_no}</h1>
        <Badge className={`${statusColors[report.status] || 'bg-[#2C2C2E] text-white'} text-xs ml-auto`}>
          {report.status}
        </Badge>
      </header>

      <main className="p-4 max-w-3xl mx-auto space-y-4">
        {/* Video Player */}
        <Card className="bg-[#1C1C1E] border-[#2C2C2E]">
          <CardContent className="p-4">
            {videoUrl ? (
              <video
                data-testid="video-player"
                src={videoUrl}
                controls
                className="w-full rounded-lg bg-black"
                style={{ maxHeight: '400px' }}
              />
            ) : (
              <div className="w-full h-48 bg-[#0A0A0A] rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Play weight="fill" className="w-12 h-12 text-[#666] mx-auto mb-2" />
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    {report.video_deleted ? 'Video süresi dolmuş ve silinmiş' : 'Video yüklenemedi'}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Report Details */}
        <Card className="bg-[#1C1C1E] border-[#2C2C2E]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading text-white">İHBAR DETAYLARI</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="overline text-[hsl(var(--muted-foreground))]">Kurum</p>
                <p className="text-white">{report.institution_name}</p>
              </div>
              <div>
                <p className="overline text-[hsl(var(--muted-foreground))]">Tarih</p>
                <p className="text-white flex items-center gap-1"><Clock weight="bold" className="w-3 h-3" />{formatDate(report.created_at)}</p>
              </div>
              {report.reporter && (
                <div>
                  <p className="overline text-[hsl(var(--muted-foreground))]">İhbar Eden</p>
                  <p className="text-white flex items-center gap-1"><User weight="bold" className="w-3 h-3" />{report.reporter.full_name}</p>
                </div>
              )}
              {report.latitude && (
                <div>
                  <p className="overline text-[hsl(var(--muted-foreground))]">Konum</p>
                  <p className="text-white flex items-center gap-1"><MapPin weight="bold" className="w-3 h-3" />{report.latitude.toFixed(4)}, {report.longitude.toFixed(4)}</p>
                </div>
              )}
              {report.video_duration_seconds && (
                <div>
                  <p className="overline text-[hsl(var(--muted-foreground))]">Video Süresi</p>
                  <p className="text-white">{Math.round(report.video_duration_seconds)} saniye</p>
                </div>
              )}
            </div>
            {report.notes_from_reporter && (
              <div>
                <p className="overline text-[hsl(var(--muted-foreground))]">Vatandaş Notu</p>
                <p className="text-white text-sm">{report.notes_from_reporter}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status Update - only for officer/admin */}
        {(user?.role === 'officer' || user?.role === 'admin') && report.status !== 'Kapatıldı' && (
          <Card className="bg-[#1C1C1E] border-[#2C2C2E]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading text-white">DURUM GÜNCELLE</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="status-select" className="h-12 bg-[#0A0A0A] border-[#2C2C2E] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1E] border-[#2C2C2E]">
                  <SelectItem value="Yeni" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">Yeni</SelectItem>
                  <SelectItem value="İnceleniyor" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">İnceleniyor</SelectItem>
                  <SelectItem value="İşleme Alındı" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">İşleme Alındı</SelectItem>
                  <SelectItem value="Kapatıldı" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">Kapatıldı</SelectItem>
                </SelectContent>
              </Select>
              <Input
                data-testid="status-note-input"
                value={statusNote}
                onChange={e => setStatusNote(e.target.value)}
                placeholder="Not ekleyin (istege bagli)"
                className="h-12 bg-[#0A0A0A] border-[#2C2C2E] text-white placeholder:text-[#666]"
              />
              <Button
                data-testid="update-status-button"
                onClick={handleStatusUpdate}
                disabled={updating || newStatus === report.status}
                className="w-full h-12 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white font-heading uppercase"
              >
                {updating ? 'Güncelleniyor...' : 'Durumu Güncelle'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Status History */}
        {report.status_logs?.length > 0 && (
          <Card className="bg-[#1C1C1E] border-[#2C2C2E]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading text-white flex items-center gap-2">
                <Note weight="bold" className="w-4 h-4" /> DURUM GEÇMİŞİ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {report.status_logs.map(log => (
                  <div key={log.id} className="border-l-2 border-[#2C2C2E] pl-3">
                    <div className="flex items-center gap-2">
                      {log.old_status && (
                        <Badge variant="outline" className="text-xs border-[#2C2C2E] text-[hsl(var(--muted-foreground))]">{log.old_status}</Badge>
                      )}
                      {log.old_status && <span className="text-[hsl(var(--muted-foreground))]">&rarr;</span>}
                      <Badge className={`${statusColors[log.new_status] || 'bg-[#2C2C2E] text-white'} text-xs`}>{log.new_status}</Badge>
                    </div>
                    {log.note && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{log.note}</p>}
                    <p className="text-xs text-[#666] mt-1">{formatDate(log.created_at)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
